import { ScoreRange } from '@vizit/constants';
import { cva, cx } from '@vizit/theme';
import { getScoreRange, serializeScore } from '@vizit/utils';
import * as React from 'react';
import { ProgressBar as AriaProgressBar, type ProgressBarProps as AriaProgressBarProps } from 'react-aria-components';
import { Skeleton } from '../skeleton';
import { getLikertLabel, getLikertScoreRange } from './score-bar.utils';

type ScoreBarLabelType = 'score' | 'likert';

interface ScoreBarProps extends Omit<AriaProgressBarProps, 'value' | 'minValue' | 'maxValue'> {
  /**
   * Score value to display (0-100).
   *
   * @optional
   * @type number | null
   * @default null
   */
  score: number | null;
  /**
   * Whether the component is in hover state.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isHovered?: boolean;
  /**
   * Whether the component is in loading state.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the asset is unscored (omit_score). Shows "Informational" instead of loading skeleton.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isUnscored?: boolean;
  /**
   * Label to display when the asset is unscored.
   *
   * @optional
   * @type {'Unscored' | 'Informational'}
   * @default 'Informational'
   */
  unscoredLabel?: 'Unscored' | 'Informational';
  /**
   * Label display type. 'score' shows the numeric value, 'likert' shows Low/Medium/High.
   *
   * @optional
   * @type 'score' | 'likert'
   * @default 'score'
   */
  labelType?: ScoreBarLabelType;
  /**
   * Whether to use mobile layout: label moves to the right with score-range color.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isMobile?: boolean;
}

interface DivProps extends React.HTMLAttributes<HTMLDivElement> {
  score: number | null;
  isHovered?: boolean;
}

const mobileLabelColor: Record<ScoreRange, string> = {
  [ScoreRange.VeryLow]: 'text-error-primary',
  [ScoreRange.Low]: 'text-error-primary',
  [ScoreRange.Moderate]: 'text-warning-secondary',
  [ScoreRange.High]: 'text-success-secondary',
  [ScoreRange.VeryHigh]: 'text-success-secondary',
  [ScoreRange.NotApplicable]: 'text-tertiary',
  [ScoreRange.Unscored]: 'text-tertiary',
};

const styles = {
  container: cva({
    base: 'flex flex-col items-start justify-center w-full',
    variants: {
      isHovered: {
        true: 'bg-gray-utility-900',
        false: 'bg-gray-utility-50',
      },
      isMobile: {
        true: 'bg-transparent h-[27px] px-[4px]',
        false: 'px-3 h-[29px]',
      },
    },
    defaultVariants: {
      isHovered: false,
      isMobile: false,
    },
  }),
  root: cva({
    base: 'flex items-center gap-4 w-full',
  }),
  label: cva({
    base: 'font-mono text-base leading-[18px] font-normal whitespace-nowrap shrink-0',
    variants: {
      isHovered: {
        true: 'text-white',
        false: '',
      },
      isUnscored: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        isHovered: false,
        isUnscored: false,
        className: 'text-tertiary',
      },
      {
        isHovered: false,
        isUnscored: true,
        className: 'text-tertiary',
      },
    ],
    defaultVariants: {
      isHovered: false,
      isUnscored: false,
    },
  }),
  trackContainer: cva({
    base: 'relative flex-1 h-[6px] overflow-hidden',
    variants: {
      isHovered: {
        true: 'bg-gray-utility-700',
        false: 'bg-gray-utility-200',
      },
    },
    defaultVariants: {
      isHovered: false,
    },
  }),
  fill: cva({
    base: 'h-full transition-all',
    variants: {
      range: {
        [ScoreRange.VeryLow]: 'bg-gradient-to-r from-error-400 to-error-600',
        [ScoreRange.Low]: 'bg-gradient-to-r from-error-200 to-error-500',
        [ScoreRange.Moderate]: 'bg-gradient-to-r from-yellow-200 to-yellow-400',
        [ScoreRange.High]: 'bg-gradient-to-r from-green-yellow-200 to-green-yellow-400',
        [ScoreRange.VeryHigh]: 'bg-gradient-to-r from-success-200 to-success-400',
        [ScoreRange.NotApplicable]: 'bg-gray-utility-300',
        [ScoreRange.Unscored]: 'bg-gray-utility-300',
      },
    },
    defaultVariants: {
      range: ScoreRange.NotApplicable,
    },
  }),
};

/**
 * A progress bar component to display a score value with a visual progress indicator.
 *
 * @param {ScoreBarProps} - The props to use for the score bar.
 */
function ScoreBar(props: ScoreBarProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    className,
    score,
    isHovered = false,
    isLoading = false,
    isUnscored = false,
    unscoredLabel = 'Informational',
    labelType = 'score',
    isMobile = false,
    id,
    style,
    ...other
  } = props;

  const scoreRange = labelType === 'likert' ? getLikertScoreRange(score) : getScoreRange(score);
  const displayScore = labelType === 'likert' ? getLikertLabel(score, isMobile) : serializeScore(score);
  const isScored = score !== null && score !== undefined;

  // Loading state (also shown when score is null and not explicitly unscored)
  if (isLoading || (!isScored && !isUnscored)) {
    return (
      <div
        ref={ref}
        className={cx(styles.container({ isHovered, isMobile }), className)}
        id={id}
        style={typeof style === 'function' ? undefined : style}
      >
        <div className={styles.root()}>
          <Skeleton className="h-[18px] w-10 rounded-none" variant="square" />
          <Skeleton className="h-[6px] flex-1 rounded-[1px]" variant="square" />
        </div>
      </div>
    );
  }

  // Unscored (omit_score) — show "Informational" label
  if (!isScored) {
    const divProps: DivProps = {
      score,
      className: cx(styles.container({ isHovered, isMobile }), className),
      id,
      style: typeof style === 'function' ? undefined : style,
    };
    return (
      <div ref={ref} {...divProps}>
        <div className={styles.root()}>
          <span className={cx(styles.label({ isHovered, isUnscored: true }), '!font-sans italic text-xs')}>
            {unscoredLabel}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cx(
        styles.container({ isHovered, isMobile }),
        labelType === 'likert' && !isHovered && !isMobile && 'bg-gray-dark-25',
        className,
      )}
      id={id}
      style={typeof style === 'function' ? undefined : style}
    >
      <AriaProgressBar
        value={score}
        minValue={0}
        maxValue={100}
        className={styles.root()}
        aria-label={`Score: ${displayScore}`}
        {...other}
      >
        {({ percentage }) => (
          <>
            {!isMobile && (
              <span
                className={
                  labelType === 'likert'
                    ? cx(
                        'font-mono text-xs font-normal whitespace-nowrap shrink-0',
                        isHovered ? 'text-white' : 'text-gray-utility-700',
                      )
                    : styles.label({ isHovered, isUnscored: false })
                }
              >
                {displayScore}
              </span>
            )}
            <div className={styles.trackContainer({ isHovered })}>
              <div className={styles.fill({ range: scoreRange })} style={{ width: `${percentage}%` }} />
            </div>
            {isMobile && (
              <span
                className={cx(
                  'font-mono text-xs font-normal whitespace-nowrap shrink-0 uppercase w-[48px] text-right',
                  mobileLabelColor[scoreRange] || 'text-tertiary',
                )}
              >
                {displayScore}
              </span>
            )}
          </>
        )}
      </AriaProgressBar>
    </div>
  );
}

const ScoreBarWithRef = React.forwardRef(ScoreBar);

export type { ScoreBarProps };
export { ScoreBarWithRef as ScoreBar };
